
import { tidalTransitionExpand } from '@tidal/styles-animation';
import { tidalLink } from '@tidal/styles-link';
import tidalIcon from '@tidal/styles-icons';
import { generalAdd, generalArrowDown } from '@tidal/styles-icons';

export default {
    components: {
        tidalTransitionExpand,
        tidalLink,
        tidalIcon,
    },
    props: {
        title: {
            type: String,
            default: null,
        },
        bold: {
            type: Boolean,
            default: true,
        },
        borders: {
            type: Boolean,
            default: true,
        },
        compact: {
            type: Boolean,
            default: false,
        },
        compactInner: {
            type: [Boolean, String],
            default: false,
            /**
             * @param {unknown} val
             * @returns {boolean}
             */
            validator: val => typeof val === 'boolean' || (typeof val === 'string' && ['horizontal', 'vertical', 'all'].includes(val)),
        },
        compactWidth: {
            type: Boolean,
            default: false,
        },
        startOpen: {
            type: Boolean,
            default: false,
        },
        arrow: {
            type: Boolean,
            default: false,
        },
        iconSize: {
            type: String,
            default: 'medium',
        },
        iconRight: {
            type: Boolean,
            default: false,
        },
        /** Overrides default icon style if set. */
        iconStyle: {
            type: String,
            default: 'default',
            /**
             * @param {unknown} value
             * @returns {boolean}
             */
            validation: value => typeof(value) === 'string' && ['default', 'round'].includes(value),
        },
        noHeadings: {
            type: Boolean,
            default: false,
        },
        href: {
            type: String,
            default: null,
        },
        external: {
            type: Boolean,
            default: false,
        },
        background: {
            type: Boolean,
            default: false,
        },
        buttonClass: {
            type: String,
            default: null,
        },
        animationStyle: {
            type: String,
            default: 'default',
            /**
             * @param {unknown} val
             * @returns {boolean}
             */
            validator: val => typeof(val) === 'string' && ['default', 'slideIn'].includes(val),
        },
    },
    data() {
        return {
            /** @type {string | null} */
            uniqueID: null,
            open: this.startOpen,
            overflowHidden: false,
        };
    },
    computed: {
        /** @returns {boolean} */
        hasClickEvent() {
            return Boolean(this.$listeners && this.$listeners.click);
        },
        /** @returns {boolean} */
        isLink() {
            return Boolean(this.href || (this.hasClickEvent && !this.$slots.default));
        },
        /** @returns {string} */
        wrapperClasses() {
            if (this.background) {
                return 'background overflow-hidden border-radius-huge';
            }

            const padding = this.borders && !this.compactHorizontal ? 'pl-small pr-small' : 'pl-0';
            return this.borders ? `borders ${padding}` : padding;
        },
        /** @returns {string} */
        titleClasses() {
            const bold = this.bold ? 'font-weight-bold' : '';
            let size = '';
            if (this.compact || this.background) {
                size = 'font-size-regular';
                if (this.background && this.compact) {
                    size = 'font-size-capital';
                }
            }

            return [bold, size].filter(Boolean).join(' ');
        },
        /** @returns {string} */
        iconClasses() {
            const active = this.open && !this.isLink ? 'is-active' : '';
            const arrow = this.arrow ? 'is-arrow' : '';
            const compact = this.compact ? 'is-compact' : '';
            const iconStyle = this.background || this.iconStyle === 'round' ? 'round' : '';
            let iconMargin = '';
            if (this.title || this.$slots.titleContent) {
                const marginDirection = this.iconRight ? 'ml' : 'mr';
                iconMargin = this.background ? `${marginDirection}-regular` : `${marginDirection}-small`;
            }
            return ['icon', active, arrow, compact, iconMargin, iconStyle].filter(Boolean).join(' ');
        },
        /** @returns {boolean} */
        slideIn() {
            return this.animationStyle === 'slideIn';
        },
        /** @returns {boolean} */
        compactVertical() {
            return this.compactInner === true || this.compactInner === 'vertical' || this.compactInner === 'all';
        },
        /** @returns {boolean} */
        compactHorizontal() {
            return this.compactInner === 'horizontal' || this.compactInner === 'all';
        },
        /** @returns {string} */
        currentIcon() {
            if (this.isLink) {
                return this.icons.linkArrow;
            }

            return this.arrow ? this.icons.generalArrowDown : this.icons.generalAdd;
        },
        icons() {
            return {
                generalAdd,
                generalArrowDown,
                linkArrow: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M16.9806 14.0397C16.9806 14.535 16.6096 14.9149 16.1733 14.9149C15.7372 14.9149 15.3856 14.5282 15.3856 14.0533V11.4955L15.4897 8.97162L14.4481 10.1861L8.43245 16.4482C8.25667 16.6314 8.05485 16.7263 7.84001 16.7263C7.40381 16.7263 7.01318 16.3057 7.01318 15.8647C7.01318 15.6476 7.11084 15.4237 7.28011 15.2473L13.2892 8.97841L14.4546 7.89967L11.9351 7.99465H9.57178C9.12256 7.99465 8.75146 7.62829 8.75146 7.18051C8.75146 6.72594 9.10303 6.34601 9.5848 6.34601H16.1213C16.6356 6.34601 16.9741 6.70559 16.9741 7.23478L16.9806 14.0397Z" /></svg>',
            };
        },
    },
    watch: {
        /** @type {import('vue').WatchHandler<boolean>} */
        open(open) {
            this.$emit('toggle', open);
        },
    },
    mounted() {
        this.uniqueID = Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5);
    },
};
